import { useCallback, useMemo, useState } from "react";

import { getErrorMessage, useGetPaginatedActivities } from "~/v2/api/hooks";
import type { Product } from "~/v2/api/rest/account";
import { matchesUnderlying } from "../../shared/symbolMatch";
import {
  ACTIVITIES_REQUEST_PAGE_SIZE,
  type ActivitiesTypeFilter,
  type ActivityRow,
  activityTypesForFilter,
  getActivitiesPage,
  toActivityRow,
} from "./activities.utils";

export interface UseActivitiesControllerParams {
  accountID: string | undefined;
  product: Product | undefined;
  symbol: string | undefined;
}

export interface ActivitiesControllerValue {
  rows: ActivityRow[];
  symbol?: string;
  scopeNotice?: string;
  typeFilter: ActivitiesTypeFilter;
  onTypeFilterChange: (value: ActivitiesTypeFilter) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  isAccountReady: boolean;
  pageNumber: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

interface ActivitiesPaginationState {
  contextKey: string;
  cursors: string[];
}

const EMPTY_CURSOR_HISTORY: string[] = [];

export const useActivitiesController = ({
  accountID,
  product,
  symbol,
}: UseActivitiesControllerParams): ActivitiesControllerValue => {
  const isAccountReady = Boolean(accountID && product);

  const [typeFilter, setTypeFilter] = useState<ActivitiesTypeFilter>("all");
  const contextKey = JSON.stringify({
    accountID,
    product,
    typeFilter,
  });
  const [pagination, setPagination] = useState<ActivitiesPaginationState>({
    contextKey,
    cursors: [],
  });
  const cursorHistory =
    pagination.contextKey === contextKey
      ? pagination.cursors
      : EMPTY_CURSOR_HISTORY;
  const activeToken = cursorHistory.at(-1);

  const { data, error, isError, isLoading, isFetching, refetch } =
    useGetPaginatedActivities({
      accountID,
      paper: product === "paper",
      pageSize: ACTIVITIES_REQUEST_PAGE_SIZE,
      pageToken: activeToken,
      filters: {
        activityTypes: activityTypesForFilter(typeFilter),
      },
      options: {
        enabled: isAccountReady,
        meta: { showErrorToast: false },
      },
    });

  const { activities, nextToken } = getActivitiesPage(data);

  const rows = useMemo(() => {
    const filtered = symbol
      ? activities.filter((activity) =>
          typeof activity.symbol === "string" && activity.symbol
            ? matchesUnderlying(symbol, activity.symbol)
            : false,
        )
      : activities;
    return filtered.map(toActivityRow);
  }, [activities, symbol]);

  const onTypeFilterChange = useCallback((value: ActivitiesTypeFilter) => {
    setTypeFilter(value);
  }, []);

  const onPreviousPage = useCallback(() => {
    setPagination((current) => {
      if (current.contextKey !== contextKey) {
        return { contextKey, cursors: [] };
      }
      return {
        contextKey,
        cursors: current.cursors.slice(0, -1),
      };
    });
  }, [contextKey]);

  const onNextPage = useCallback(() => {
    if (!nextToken) {
      return;
    }
    setPagination((current) => ({
      contextKey,
      cursors: [
        ...(current.contextKey === contextKey ? current.cursors : []),
        nextToken,
      ],
    }));
  }, [contextKey, nextToken]);

  return {
    rows,
    symbol,
    scopeNotice: symbol
      ? "Underlying and option matches are filtered within each server page; more matches may appear on later pages."
      : undefined,
    typeFilter,
    onTypeFilterChange,
    isLoading,
    isFetching,
    isError,
    errorMessage: error ? getErrorMessage(error) : undefined,
    onRetry: refetch,
    isAccountReady,
    pageNumber: cursorHistory.length + 1,
    hasPreviousPage: cursorHistory.length > 0,
    hasNextPage: Boolean(nextToken),
    onPreviousPage,
    onNextPage,
  };
};
